import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {SignInContext} from "../../context/SignInContext";
import {ProjectRepository} from "../../repositories/ProjectRepository";
import {ProjectRichTableView} from "./components/project-rich-table-view";
import ProjectsPageTour from "../../components/tour/ProjectsPageTour";

const PAGE_SIZE = 5;

// filters: {organizationId, type}; both optional
const EMPTY_FILTERS = {organizationId: null, type: null};

export const Projects = () => {
  const {currentUser} = useContext(SignInContext);
  const [projects, setProjects] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [shouldRunTour, setShouldRunTour] = useState(() => {
    return sessionStorage.getItem("tour") === "true";
  });
  // Latest request, so a slower response for an older page or filter can't replace the current one
  const requestRef = useRef(0);

  useEffect(() => {
    if (shouldRunTour) {
      sessionStorage.removeItem("tour");
    }
  }, [shouldRunTour]);

  // /paged returns the projects the user can view (every project for admins), filtered and paged by the server
  const fetchProjects = useCallback(async (signal) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const response = await ProjectRepository.getPaged({
        page, size: PAGE_SIZE, organizationId: filters.organizationId, type: filters.type, signal,
      });
      if (requestRef.current !== requestId) return;
      const data = response.data || {};
      setProjects(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.name === "AbortError") return;
      console.error(e);
      toast.error(e?.response?.data?.message || "Failed to load projects");
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    if (!currentUser) return;
    const controller = new AbortController();
    fetchProjects(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.username, fetchProjects]);

  // a filter change starts again on the first page
  const changeFilters = (changes) => {
    setFilters(prev => ({...prev, ...changes}));
    setPage(0);
  };

  const handleTourFinish = () => {
    sessionStorage.removeItem("tour");
    setShouldRunTour(false);
  };

  return (
      <>
        <ProjectsPageTour
          run={shouldRunTour && !loading}
          hasProjects={projects?.length > 0}
          onFinish={handleTourFinish}
        />
        <ProjectRichTableView
          projects={projects}
          loading={loading}
          page={page}
          pageSize={PAGE_SIZE}
          totalElements={totalElements}
          totalPages={totalPages}
          onChangePage={setPage}
          filters={filters}
          onChangeFilters={changeFilters}
        />
      </>
  )
}
